/**
 * ADM-050: every staff action and every clinical record access. Who, action, target, reason and
 * when. Lab values never appear here — they live on the patient record, which itself is logged
 * when someone opens it.
 */
import * as stylex from "@stylexjs/stylex";
import type { FormEvent } from "react";
import { useState } from "react";
import type { AdminAuditLog, AdminAuditLogRow } from "@lattice/core";
import { ROUTES } from "@lattice/core";
import "@lattice/tokens/color.stylex";
import { color } from "@lattice/tokens/color.stylex";
import "@lattice/tokens/space.stylex";
import { space } from "@lattice/tokens/space.stylex";
import { api } from "./api.ts";
import { Person, RecordLink, Truncate } from "./bits.tsx";
import type { Column } from "./layout.tsx";
import { PageHead, Section, Table } from "./layout.tsx";
import { useLoad } from "./load.ts";
import { href, navigate } from "./router.ts";
import { Button, ErrorText, Field } from "./ui.tsx";
import { action, moment, refusal, targetType } from "./words.ts";
type Filter = {
  readonly actor: string;
  readonly patient: string;
  readonly from: string;
  readonly to: string;
};
function sameFilter(left: Filter, right: Filter): boolean {
  return left.actor === right.actor && left.patient === right.patient && left.from === right.from && left.to === right.to;
}
function targetCell(row: AdminAuditLogRow) {
  const label = `${targetType(row.targetType)}: ${row.target ?? row.targetId}`;
  if (row.targetType === "user") return <RecordLink to={href.user(row.targetId)} relation="Account" label={row.target ?? row.targetId} />;
  if (row.targetType === "patient") return <RecordLink to={href.patient(row.targetId)} relation="Patient" label={row.target ?? "Patient record"} />;
  return <Truncate value={label} />;
}
const COLUMNS: readonly Column<AdminAuditLogRow>[] = [{
  label: "When",
  numeric: true,
  nowrap: true,
  cell: row => <time dateTime={row.createdAt} className="x1jty25b">
        {moment(row.createdAt)}
      </time>
}, {
  label: "Who",
  cell: row => row.actorUserId === null || row.actorEmail === null ? <span className="x1jty25b">The system</span> : <Person name={row.actorEmail} email={row.actorEmail} to={href.user(row.actorUserId)} />
}, {
  label: "Action",
  nowrap: true,
  cell: row => action(row.action)
}, {
  label: "Target",
  phoneLabel: true,
  cell: targetCell
}, {
  label: "Reason",
  wide: true,
  phoneLabel: true,
  cell: row => row.reason === null ? <span className="x1jty25b">No reason recorded</span> : row.reason
}];
function Filters({
  filter
}: {
  readonly filter: Filter;
}) {
  const [draft, setDraft] = useState<{
    readonly for: Filter;
    readonly value: Filter;
  }>({
    for: filter,
    value: filter
  });
  const value = sameFilter(draft.for, filter) ? draft.value : filter;
  function submit(event: FormEvent) {
    event.preventDefault();
    navigate(href.audit({
      actor: value.actor.trim(),
      patient: value.patient.trim(),
      from: value.from,
      to: value.to
    }));
  }
  function set(field: keyof Filter, next: string) {
    setDraft({
      for: filter,
      value: {
        ...value,
        [field]: next
      }
    });
  }
  return <form onSubmit={submit} className="x78zum5 xdt5ytf x15agyyk">
      <div className="x78zum5 x1a02dak x1r0x45p xpqajaz">
        <div className="xehxkzz xuu4ctt">
          <Field id="audit-actor" label="Staff member" value={value.actor} onChange={next => set("actor", next)} hint="Email or id" />
        </div>
        <div className="xehxkzz xuu4ctt">
          <Field id="audit-patient" label="Patient" value={value.patient} onChange={next => set("patient", next)} hint="Patient id. Filters the log. Does not open the record." />
        </div>
        <div className="xehxkzz xuu4ctt">
          <Field id="audit-from" label="From" type="date" value={value.from} onChange={next => set("from", next)} />
        </div>
        <div className="xehxkzz xuu4ctt">
          <Field id="audit-to" label="To" type="date" value={value.to} onChange={next => set("to", next)} />
        </div>
      </div>
      <div className="x78zum5 x1a02dak x1173whf">
        <Button type="submit" tone="quiet">
          Filter
        </Button>
      </div>
    </form>;
}
function List({
  result,
  filter
}: {
  readonly result: AdminAuditLog;
  readonly filter: Filter;
}) {
  return <>
      <PageHead title="Audit log" meta={`${result.events.length} events`} />
      <div className="x78zum5 xdt5ytf x15agyyk">
        <p className="x1ghz6dp x1jty25b x15g8yj6">
          Every lock, link change and record opened, with who did it and why. Lab values and
          clinical results are not in this log.
        </p>
        <Filters filter={filter} />
        <Section title="Events" count={result.events.length}>
          <Table label="Audit events" columns={COLUMNS} rows={result.events} rowKey={row => row.id} empty={filter.actor === "" && filter.patient === "" && filter.from === "" && filter.to === "" ? "No staff actions have been recorded yet." : "No events match these filters. Clear them to see the full log."} />
        </Section>
      </div>
    </>;
}
export function Audit(filter: Filter) {
  const [found] = useLoad(`audit-${filter.actor}-${filter.patient}-${filter.from}-${filter.to}`, () => api<AdminAuditLog>(ROUTES.adminAudit, {
    query: {
      actor: filter.actor,
      patient: filter.patient,
      from: filter.from,
      to: filter.to
    }
  }));
  if (found.state === "loading") return <PageHead title="Audit log" meta="Loading" />;
  if (found.state === "failed") return <>
        <PageHead title="Audit log" />
        <ErrorText>{refusal(found.error)}</ErrorText>
      </>;
  return <List result={found.data} filter={filter} />;
}